import { useAppSelector } from '../../../redux/hooks.ts'
import { selectThemeStyle } from '../../../features/setting/setting.slice.ts'
import { convertPxToNumber } from '../../../utils/helpers.ts'
import IdeyaTypography from '../../../common/antd/ideya-typography.tsx'
import { useDispatch } from 'react-redux'
import IdeyaRangePicker from '../../../common/antd/ideya-rangepicker.tsx'
import {
  selectRankingInput,
  setGroupByFirm,
  setRankingFirm,
  setRankingInputDateRange,
  setRankingRegion,
} from '../../../redux/slices/ranking-input.slice.ts'
import { RankingType } from '../../../enums/ranking-input.enum.ts'
import dayjs from 'dayjs'
import { useListFirmOptionsQuery } from '../../../apis/option/option-api.api.ts'
import { VerticalSeparator } from '../../../common/components/vertical-separator.tsx'
import {
  canSeeCategorySelectPermissions,
  canSeeFirmSelectPermissions,
  hasPermission,
} from '../../../utils/permissions.ts'
import { UserInfo } from '../../../apis/auth/auth.def.ts'
import { selectUser } from '../../../features/user/user.slice.ts'
import Spin from 'antd/es/spin/index'
import Row from 'antd/es/grid/row'
import Col from 'antd/es/grid/col'
import Segmented from 'antd/es/segmented/index'
import Select from 'antd/es/select/index'
import { useIsMobile } from '../../../common/hooks/use-is-mobile.ts'
import { getCategoryOptions } from '../../../utils/selector-options.ts'
import { useTranslation } from 'react-i18next'
import { IdeyaButton } from '../../../common/antd/ideya-button.tsx'
import { SyncOutlined } from '@ant-design/icons'
import Input from 'antd/es/input/Input'

interface OverallRankingUserControlsProps {
  refetchRanking: () => void
  searchFullName?: string
  setSearchFullName?: (value: string) => void
}

export const OverallRankingUserControls: React.FC<
  OverallRankingUserControlsProps
> = ({ refetchRanking, searchFullName, setSearchFullName }) => {
  const { t } = useTranslation()
  const themeStyle = useAppSelector(selectThemeStyle)
  const rankingInput = useAppSelector(selectRankingInput)
  const userInfo: UserInfo = useAppSelector(selectUser)
  const dispatch = useDispatch()
  const isMobile = useIsMobile(768)

  const canFilterByFirm = hasPermission(
    canSeeFirmSelectPermissions,
    userInfo.firmRole,
    userInfo.groupRole,
    userInfo.role
  )
  const canFilterByCategory = hasPermission(
    canSeeCategorySelectPermissions,
    userInfo.firmRole,
    userInfo.groupRole,
    userInfo.role
  )

  const { data: firmOptionData, isLoading: isLoadingFirmOptions } =
    useListFirmOptionsQuery()

  if (isLoadingFirmOptions) {
    return <Spin />
  }

  return (
    <>
      <Row
        gutter={[
          convertPxToNumber(themeStyle.marginBase),
          convertPxToNumber(themeStyle.marginBase),
        ]}
        style={{ marginBottom: themeStyle.marginSM, alignItems: 'end' }}
      >
        <Col xs={24} md={3}>
          {!isMobile && (
            <Row style={{ paddingBottom: themeStyle.paddingXS }}>
              <IdeyaTypography.Text>Ranking Type</IdeyaTypography.Text>
            </Row>
          )}
          <Row>
            <Segmented
              onChange={(value) => {
                const groupByFirm = value === RankingType.Company
                dispatch(setGroupByFirm(groupByFirm))
              }}
              options={[
                {
                  value: RankingType.Individual,
                  label: (
                    <div
                      style={{
                        display: 'flex',
                        flex: '1 0 0',
                        padding: '0px 12px',
                        gap: '4px',
                        alignSelf: 'stretch',
                        height: '28px',
                        width: '48px',
                        justifyContent: 'center',
                      }}
                    >
                      <div>{t('overall_ranking.user_controls.individual')}</div>
                    </div>
                  ),
                },
                {
                  value: RankingType.Company,
                  label: (
                    <div
                      style={{
                        display: 'flex',
                        flex: '1 0 0',
                        padding: '0px 12px',
                        gap: '4px',
                        alignSelf: 'stretch',
                        height: '28px',
                        width: '48px',
                        justifyContent: 'center',
                      }}
                    >
                      <div>{t('overall_ranking.user_controls.company')}</div>
                    </div>
                  ),
                },
              ]}
            />
          </Row>
        </Col>
        {!isMobile && (
          <Col>
            <VerticalSeparator />
          </Col>
        )}
        {canFilterByCategory && (
          <Col xs={24} md={4}>
            {!isMobile && (
              <Row style={{ paddingBottom: themeStyle.paddingXS }}>
                <IdeyaTypography.Text>
                  {t('common.user_controls.category')}
                </IdeyaTypography.Text>
              </Row>
            )}
            <Row>
              <Select
                showSearch
                value={rankingInput.region}
                style={{ width: '100%' }}
                onChange={(value) => dispatch(setRankingRegion(value))}
                options={getCategoryOptions(isMobile)}
              />
            </Row>
          </Col>
        )}
        {canFilterByFirm && (
          <Col xs={24} md={4}>
            {!isMobile && (
              <Row style={{ paddingBottom: themeStyle.paddingXS }}>
                <IdeyaTypography.Text>
                  {t('common.user_controls.firm')}
                </IdeyaTypography.Text>
              </Row>
            )}
            <Row>
              <Select
                showSearch
                value={rankingInput.firm}
                style={{ width: '100%' }}
                onChange={(value) => dispatch(setRankingFirm(value))}
                options={[
                  {
                    value: '',
                    label: isMobile
                      ? t('common.user_controls.all_firms')
                      : t('common.user_controls.all'),
                  },
                  ...(firmOptionData?.data.map((firm) => ({
                    value: firm.value,
                    label: firm.label,
                  })) ?? []),
                ]}
                filterOption={(input, option) =>
                  (option?.label as string)
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Row>
          </Col>
        )}
        <Col xs={24} md={5}>
          {!isMobile && (
            <Row style={{ paddingBottom: themeStyle.paddingXS }}>
              <IdeyaTypography.Text>
                {t('common.user_controls.time_interval')}
              </IdeyaTypography.Text>
            </Row>
          )}
          <Row>
            <IdeyaRangePicker
              style={{ width: '100%' }}
              value={[
                dayjs(rankingInput.dateRange[0]),
                dayjs(rankingInput.dateRange[1]),
              ]}
              onChange={(dates) => {
                dispatch(
                  setRankingInputDateRange([
                    dates?.[0] ? dates[0].format('YYYY-MM-DD') : null,
                    dates?.[1] ? dates[1].format('YYYY-MM-DD') : null,
                  ])
                )
              }}
            />
          </Row>
        </Col>
        <Col>
          <IdeyaButton onClick={refetchRanking} icon={<SyncOutlined />} />
        </Col>
        {!isMobile && (
          <Col>
            <VerticalSeparator />
          </Col>
        )}
        <Col>
          {!isMobile && (
            <Row style={{ paddingBottom: themeStyle.paddingXS }}>
              <IdeyaTypography.Text>
                {t('common.user_controls.search')}
              </IdeyaTypography.Text>
            </Row>
          )}
          <Row>
            <Input
              placeholder={t('common.user_controls.search_name')}
              value={searchFullName}
              onChange={(e) =>
                setSearchFullName
                  ? setSearchFullName(e.target.value)
                  : undefined
              }
              allowClear
            />
          </Row>
        </Col>
      </Row>
    </>
  )
}
